import React from 'react';

import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';
import GeneralAccessSelect from './GeneralAccessSelect';
import TicketAccessAddPeople from './TicketAccessAddPeople';
import TicketAccessMembers from './TicketAccessMembers';

interface Props {
    ticketKey?: TicketKey;
    onClose?: () => void;
}

/**
 * Everything that decides who can reach the ticket: its members, and whether
 * colleagues who are not members can read it. Changes apply as they are made, so
 * the footer only dismisses.
 */
const TicketAccessModal = ({ ticketKey, onClose }: Props) => {
    const { i18next } = useAppContext();
    // The two halves are separate rights: who may change the member list, and who
    // may widen the ticket beyond it. They resolve together today and may not
    // later — promoting a ticket to company-wide is the likelier one to restrict.
    const canManageMembers = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'manageMembers'),
    );
    const canSetVisibility = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setVisibility'),
    );

    return (
        <BaseModal
            open={true}
            header={i18next.t('tickets.access.title')}
            onClose={onClose}
            // A height floor, so a ticket with two members does not open a squat
            // dialog; min() keeps it under the panel's own 80dvh cap, which a
            // plain min-height would win against on a short viewport.
            //
            // The darker surface is for dark mode, where gray-200 — BaseModal's
            // own — resolves to the same #2c2c2c the fields inside it use, so
            // every control melted into the dialog. A step down leaves the ladder
            // legible: dialog #1a1a1a, fields #2c2c2c, floating panels #3a3a3a.
            className="min-h-[min(480px,80dvh)] dark:bg-gray-100"
            modalSize={modalSizes.SMALL_MEDIUM}
            hideCancelButton={true}
            confirmButtonText={i18next.t('done')}
            onSubmit={() => onClose?.()}
            // Takes the scroll off the modal body. With two scrollers the member
            // list scrolled inside a body that scrolled too, and the general-access
            // hint below it ended up under the footer edge. Here the list is the
            // only thing that scrolls, so the hint always stays in view.
            mainClassName="flex flex-col overflow-hidden"
        >
            <div className="flex min-h-0 flex-1 flex-col gap-y-6">
                {canManageMembers ? (
                    <div className="shrink-0">
                        <TicketAccessAddPeople ticketKey={ticketKey} />
                    </div>
                ) : null}
                <TicketAccessMembers
                    ticketKey={ticketKey}
                    canEdit={canManageMembers}
                />
                <div className="shrink-0">
                    <GeneralAccessSelect
                        ticketKey={ticketKey}
                        canEdit={canSetVisibility}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default TicketAccessModal;
